import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Sparkles, TrendingUp, Heart, Smile, PartyPopper, ThumbsUp, Music, Hand, Star, Laugh, Coffee, Flame, Zap, Gift, Award, Brain, Clock, Rocket, Ghost, PawPrint, Pizza } from 'lucide-react';

interface GifPickerProps {
  onSelect: (gifUrl: string) => void;
  onClose: () => void;
}

interface GiphyGif {
  id: string;
  title: string;
  images: {
    original: { url: string };
    fixed_width: { url: string };
  };
}

// Replaces Tenor, which Google discontinued on 2026-06-30.
const GIPHY_BASE_URL = 'https://api.giphy.com/v1/gifs';
const GIPHY_API_KEY = import.meta.env.VITE_GIPHY_API_KEY || '';

// Beta keys allow 100 requests/hour across the whole app, so results are cached
// per query for the life of the tab to keep repeat opens from spending quota.
const GIPHY_RATING = 'pg-13';
const resultCache = new Map<string, GiphyGif[]>();

export const GifPicker: React.FC<GifPickerProps> = ({ onSelect, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [gifs, setGifs] = useState<GiphyGif[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('trending');
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  // Expanded categories with icons
  const categories = [
    { name: 'Trending', value: 'trending', icon: TrendingUp },
    { name: 'Reactions', value: 'reactions', icon: Smile },
    { name: 'Happy', value: 'happy excited', icon: PartyPopper },
    { name: 'Love', value: 'love heart', icon: Heart },
    { name: 'Thumbs Up', value: 'thumbs up approve', icon: ThumbsUp },
    { name: 'Celebrate', value: 'celebrate party', icon: Star },
    { name: 'Laughing', value: 'laughing lol funny', icon: Laugh },
    { name: 'Dance', value: 'dance dancing', icon: Music },
    { name: 'Clapping', value: 'clapping applause', icon: Hand },
    { name: 'Mind Blown', value: 'mind blown shocked', icon: Brain },
    { name: 'Thank You', value: 'thank you thanks', icon: Gift },
    { name: 'Good Job', value: 'good job well done', icon: Award },
    { name: 'Waiting', value: 'waiting impatient', icon: Clock },
    { name: 'Excited', value: 'excited pumped', icon: Rocket },
    { name: 'Sad', value: 'sad crying', icon: Ghost },
    { name: 'Yes', value: 'yes agree nod', icon: Zap },
    { name: 'No', value: 'no disagree shake head', icon: X },
    { name: 'Coffee', value: 'coffee morning tired', icon: Coffee },
    { name: 'Fire', value: 'fire lit amazing', icon: Flame },
    { name: 'Cute', value: 'cute adorable aww', icon: PawPrint },
    { name: 'Dogs', value: 'dog puppy', icon: PawPrint },
    { name: 'Cats', value: 'cat kitten', icon: PawPrint },
    { name: 'Food', value: 'food eating yum', icon: Pizza },
    { name: 'Office', value: 'office work meeting', icon: Coffee },
  ];

  const fetchGifs = useCallback(async (query: string) => {
    if (!GIPHY_API_KEY) {
      setGifs([]);
      setError('GIFs aren\u2019t set up yet. An admin needs to add a GIPHY API key.');
      return;
    }

    const cached = resultCache.get(query);
    if (cached) {
      setGifs(cached);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        api_key: GIPHY_API_KEY,
        limit: '40',
        rating: GIPHY_RATING,
      });
      if (query) params.set('q', query);

      const endpoint = query ? 'search' : 'trending';
      const response = await fetch(`${GIPHY_BASE_URL}/${endpoint}?${params}`);

      if (response.status === 429) {
        throw new Error('rate-limit');
      }
      if (response.status === 401 || response.status === 403) {
        throw new Error('bad-key');
      }
      if (!response.ok) {
        throw new Error(`GIPHY returned ${response.status}`);
      }

      const body = await response.json();
      const results: GiphyGif[] = body.data || [];
      resultCache.set(query, results);
      setGifs(results);
    } catch (err) {
      console.error('Error fetching GIFs:', err);
      setGifs([]);
      const reason = err instanceof Error ? err.message : '';
      setError(
        reason === 'rate-limit'
          ? 'GIF search limit reached for this hour. Try again shortly.'
          : reason === 'bad-key'
            ? 'The GIF service rejected our API key. An admin needs to check it.'
            : 'Could not load GIFs. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // One request per view. Typing is debounced; category switches fire at once.
  useEffect(() => {
    const typed = searchTerm.trim();
    const query = typed || (selectedCategory === 'trending' ? '' : selectedCategory);

    if (!typed) {
      fetchGifs(query);
      return;
    }

    searchTimeoutRef.current = setTimeout(() => fetchGifs(query), 500);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchTerm, selectedCategory, fetchGifs]);

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category);
    setSearchTerm('');
  };

  const handleGifSelect = (gif: GiphyGif) => {
    // Use the full-size GIF URL
    onSelect(gif.images.original.url);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div className="bg-white border border-gray-200 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50 rounded-t-2xl">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-violet-100 rounded-lg">
              <Sparkles className="w-5 h-5 text-violet-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Choose a GIF</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-200 transition-colors text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search for GIFs..."
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl 
                         text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400"
            />
          </div>
        </div>

        {/* Categories - Scrollable horizontal list */}
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {categories.map((category) => {
              const Icon = category.icon;
              const isSelected = selectedCategory === category.value && !searchTerm;
              return (
                <button
                  key={category.value}
                  onClick={() => handleCategoryClick(category.value)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                    isSelected
                      ? 'bg-violet-500 text-white shadow-sm'
                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {category.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* GIF Grid */}
        <div className="flex-1 overflow-y-auto p-4 bg-white">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center gap-3">
                <div className="animate-spin rounded-full h-10 w-10 border-3 border-violet-500 border-t-transparent"></div>
                <span className="text-sm text-gray-500">Loading GIFs...</span>
              </div>
            </div>
          ) : gifs.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {gifs.map((gif) => (
                <button
                  key={gif.id}
                  onClick={() => handleGifSelect(gif)}
                  className="relative aspect-square rounded-xl overflow-hidden border border-gray-200 
                             hover:border-violet-400 hover:shadow-lg hover:scale-[1.02] transition-all duration-200 group bg-gray-50"
                >
                  <img
                    src={gif.images.fixed_width.url}
                    alt={gif.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-violet-500/0 group-hover:bg-violet-500/10 transition-colors" />
                </button>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <div className="p-4 bg-gray-100 rounded-full mb-4">
                <Sparkles className="w-10 h-10 text-gray-400" />
              </div>
              <p className="text-gray-600 font-medium">
                {error ? 'GIFs unavailable' : 'No GIFs found'}
              </p>
              <p className="text-sm text-gray-400 mt-1">
                {error || 'Try a different search term'}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex items-center justify-between">
          <span className="text-xs text-gray-400">
            {gifs.length > 0 && `${gifs.length} GIFs`}
          </span>
          <a
            href="https://giphy.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-gray-400 hover:text-violet-500 transition-colors"
          >
            Powered by GIPHY
          </a>
        </div>
      </div>
    </div>
  );
};

export default GifPicker;
